import { Accordion as BaseAccordion } from "@base-ui/react/accordion";
import * as React from "react";
import { cn } from "@/util/string";

/**
 * Premium Accordion component built on top of Base UI.
 * Features: Glassmorphism, gradient accents, smooth height animations, and modern aesthetics.
 */

interface AccordionProps
	extends Omit<
		React.ComponentPropsWithoutRef<typeof BaseAccordion.Root>,
		"value" | "onValueChange" | "defaultValue"
	> {
	/** Default values for initially expanded items */
	defaultValue?: string[];
	/** Controlled value — open panel keys */
	value?: string[];
	/** Called when panels change (for controlled mode) */
	onValueChange?: (value: string[]) => void;
	/** Whether multiple panels can be open simultaneously */
	multiple?: boolean;
}

export const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
	(
		{
			className,
			defaultValue = [],
			value: controlledValue,
			onValueChange: controlledOnValueChange,
			multiple = false,
			...props
		},
		ref,
	) => {
		const [internalValue, setInternalValue] =
			React.useState<string[]>(defaultValue);
		const value = controlledValue ?? internalValue;
		const onValueChange = controlledOnValueChange ?? setInternalValue;

		return (
			<BaseAccordion.Root
				ref={ref}
				className={cn(
					// Container styling - clean, borderless
					"flex w-full flex-col",
					// Gap between items
					"gap-4",
					className,
				)}
				value={value}
				onValueChange={(newValue) => onValueChange(newValue as string[])}
				multiple={multiple}
				{...props}
			/>
		);
	},
);
Accordion.displayName = "Accordion";

interface AccordionItemProps
	extends React.ComponentPropsWithoutRef<typeof BaseAccordion.Item> {
	/** Unique value identifying this item */
	value: string;
}

export const AccordionItem = React.forwardRef<
	HTMLDivElement,
	AccordionItemProps
>(({ className, ...props }, ref) => (
	<BaseAccordion.Item
		ref={ref}
		className={cn(
			// Individual item styling with subtle background
			"rounded-xl",
			"bg-base-200/30",
			"ring-1 ring-base-content/5",
			// Group for child hover states
			"group/item",
			// Overflow for rounded corners
			"overflow-hidden",
			className,
		)}
		{...props}
	/>
));
AccordionItem.displayName = "AccordionItem";

export const AccordionHeader = React.forwardRef<
	HTMLHeadingElement,
	React.ComponentPropsWithoutRef<typeof BaseAccordion.Header>
>(({ className, ...props }, ref) => (
	<BaseAccordion.Header ref={ref} className={cn("m-0", className)} {...props} />
));
AccordionHeader.displayName = "AccordionHeader";

interface AccordionTriggerProps
	extends React.ComponentPropsWithoutRef<typeof BaseAccordion.Trigger> {
	/** Custom icon component (optional - defaults to animated chevron) */
	icon?: React.ReactNode;
}

export const AccordionTrigger = React.forwardRef<
	HTMLButtonElement,
	AccordionTriggerProps
>(({ className, children, icon, ...props }, ref) => (
	<BaseAccordion.Trigger
		ref={ref}
		className={cn(
			// Base layout
			"group relative flex w-full items-center justify-between gap-4",
			// Generous padding
			"px-5 py-4",
			// Typography
			"text-sm font-medium text-base-content",
			// Background with hover state
			"bg-transparent",
			"hover:bg-base-content/5",
			// Active state
			"data-panel-open:bg-primary/5",
			// Remove button defaults
			"border-0 outline-none text-left",
			// Focus visible ring
			"focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50",
			// Smooth transition
			"transition-all duration-200 ease-out",
			// Cursor
			"cursor-pointer",
			className,
		)}
		{...props}
	>
		{children}
		{icon ?? (
			<ChevronIcon
				className={cn(
					"size-4 shrink-0 text-base-content/50",
					// Smooth rotation animation
					"transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
					// Rotate when open
					"group-data-panel-open:rotate-180",
					"group-data-panel-open:text-primary",
				)}
			/>
		)}
	</BaseAccordion.Trigger>
));
AccordionTrigger.displayName = "AccordionTrigger";

export const AccordionPanel = React.forwardRef<
	HTMLDivElement,
	React.ComponentPropsWithoutRef<typeof BaseAccordion.Panel>
>(({ className, children, ...props }, ref) => (
	<BaseAccordion.Panel
		ref={ref}
		className={cn(
			// Height animation using CSS variable from Base UI
			"h-(--accordion-panel-height)",
			"overflow-hidden",
			// Typography
			"text-sm text-base-content/70",
			// Smooth height transition
			"transition-[height] duration-300 ease-out",
			// Starting/ending states for animation
			"data-starting-style:h-0",
			"data-ending-style:h-0",
			className,
		)}
		{...props}
	>
		<div className="px-5 pb-5 pt-4">{children}</div>
	</BaseAccordion.Panel>
));
AccordionPanel.displayName = "AccordionPanel";

/**
 * Default chevron icon with rotation animation.
 */
function ChevronIcon(props: React.ComponentProps<"svg">) {
	return (
		<svg
			viewBox="0 0 12 12"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			{...props}
		>
			<title>Toggle</title>
			<path d="M2 4L6 8L10 4" />
		</svg>
	);
}
